import { Injectable } from '@angular/core';
import {
  ChartType,
  ChartConfiguration,
  ChartDataset,
  Point,
  ScaleOptions,
  Chart,
  ChartOptions,
} from 'chart.js';
import * as chartZoomPlugin from 'chartjs-plugin-zoom';
import { Subscription } from 'rxjs';

type RegisterChartFunction = (any) => void;

type ComputeChartFunction = (any) => void;

type UpdateChartFunction = () => void;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ComputeChartDataFunction = (any) => any;

export interface ChartYAxisProperties {
  index: number;
  color: string;
  hiddenColor: string;
  min: number;
  max: number;
  visibility: boolean;
}

export interface MultiChart {
  computed: boolean;
  chartDataSets: ChartConfiguration['data']['datasets'];
  chartOptions: ChartConfiguration['options'];
  chartType: ChartType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chartPlugins: any[];
  chartLabels: string[];
  chartLegend: boolean;
  chartId: string;
  hiddenColor: string;
  chartXAxe: ChartConfiguration['options']['scales']['x'];
  yAxisConfig: Map<number, ChartYAxisProperties>;
  chart: Chart;
  computeChartDataFunctionList: ComputeChartDataFunction[];
  computeChartData: boolean;
  subscription: Subscription;
  isViewInitialized: boolean;
  isXScaleChanged: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class MultiChartService {
  private multiCharts: Map<string, MultiChart> = new Map<string, MultiChart>();

  private sectionMultiCharts: Map<string, MultiChart> = new Map<
    string,
    MultiChart
  >();

  private getChartsMap(isSection: boolean): Map<string, MultiChart> {
    if (isSection) {
      return this.sectionMultiCharts;
    }
    return this.multiCharts;
  }

  private create(theChartId: string): MultiChart {
    const mc: MultiChart = {
      computed: false,
      chartDataSets: [],
      chartOptions: {},
      chartType: 'line',
      chartPlugins: [],
      chartLabels: [],
      chartLegend: true,
      chartId: theChartId,
      hiddenColor: 'lightgrey',
      chartXAxe: null,
      yAxisConfig: new Map<number, ChartYAxisProperties>(),
      chart: null,
      computeChartDataFunctionList: [],
      computeChartData: true,
      subscription: null,
      isViewInitialized: false,
      isXScaleChanged: false,
    };
    return mc;
  }

  private set(
    chartId: string,
    multiChart: MultiChart,
    isSection: boolean
  ): MultiChart {
    this.getChartsMap(isSection).set(chartId, multiChart);
    return multiChart;
  }

  private createChartOptions(): ChartOptions {
    const co: ChartOptions = {
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: {
          display: true,
          onClick: (event, legendItem, legend) => {
            const chart = legend.chart as Chart;
            const { datasetIndex } = legendItem;
            const dataset = chart.data.datasets[datasetIndex];
            dataset.hidden = !dataset.hidden;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { yAxisID } = dataset as any;
            if (yAxisID) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const yAxis = chart.options.scales[yAxisID] as any;

              const color = !dataset.hidden
                ? (dataset.borderColor as string)
                : 'gray';

              yAxis.ticks.color = color;

              if (!yAxis.title) {
                yAxis.title = { display: true };
              }
              yAxis.title.color = color;
            }

            chart.update();
          },
        },
        zoom: {
          pan: {
            enabled: true,
            mode: 'x',
          },
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: 'x',
          },
        },
      },
      animation: {
        duration: 0,
      },
      hover: {
        mode: 'nearest',
        intersect: true,
      },
      scales: {
        x: {
          type: 'linear',
          display: true,
        },
        y: {
          type: 'linear',
          display: true,
        },
      },
    };
    return co;
  }

  private resetChartPlugins(
    multiChart: MultiChart,
    updateChartFunction?: UpdateChartFunction
  ): void {
    // eslint-disable-next-line no-param-reassign
    multiChart.chartPlugins = [];
    multiChart.chartPlugins.push(chartZoomPlugin);
    multiChart.chartPlugins.push({
      // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
      afterUpdate: (updatedChart) => {
        // eslint-disable-next-line no-param-reassign
        multiChart.chart = updatedChart as Chart;
        // eslint-disable-next-line no-param-reassign
        multiChart.isXScaleChanged = true;
        if (updateChartFunction !== undefined) {
          updateChartFunction();
        }
      },
    });
  }

  public get(
    chartId: string,
    isSection: boolean,
    registerChartFunction?: RegisterChartFunction,
    computeChartFunction?: ComputeChartFunction,
    updateChartFunction?: UpdateChartFunction
  ): MultiChart {
    const chartsMap = this.getChartsMap(isSection);
    let multiChart = chartsMap.get(chartId);
    if (multiChart === undefined) {
      multiChart = this.create(chartId);
      this.set(chartId, multiChart, isSection);
    }
    if (multiChart.chartDataSets.length <= 0) {
      if (registerChartFunction !== undefined) {
        multiChart.chartOptions = this.createChartOptions();
        registerChartFunction(multiChart);
      }
      if (computeChartFunction !== undefined) {
        computeChartFunction(multiChart);
        // multiChart.computed = true;
      }
      this.resetChartPlugins(multiChart, updateChartFunction);
    }
    return multiChart;
  }

  public reset(isSection: boolean): void {
    const charts = this.getChartsMap(isSection);
    charts.forEach((mc) => {
      // eslint-disable-next-line no-param-reassign
      mc.chartDataSets = null;
      // eslint-disable-next-line no-param-reassign
      mc.computeChartDataFunctionList = null;
      // eslint-disable-next-line no-param-reassign
      mc.isXScaleChanged = false;
    });
    charts.clear();
  }

  public syncChartAxis(
    masterChartId: string,
    slaveChartId: string,
    isSection: boolean
  ): void {
    const slave = this.get(slaveChartId, isSection);
    if (slave.chartDataSets.length > 0) {
      const master = this.get(masterChartId, isSection);
      let beginIndex: number = null;
      let endIndex: number = null;
      for (let i = 0; i < master.chartDataSets.length; i += 1) {
        slave.yAxisConfig.set(i, { ...master.yAxisConfig.get(i) });
      }
      if (
        master.chartXAxe.min !== undefined ||
        master.chartXAxe.max !== undefined
      ) {
        for (let i = 0; i < master.chartDataSets[0].data.length; i += 1) {
          const data = master.chartDataSets[0].data[i] as Point;
          if (
            master.chartXAxe.min !== undefined &&
            beginIndex === null &&
            Number(data.x) >= Number(master.chartXAxe.min)
          ) {
            beginIndex = i;
          }
          if (master.chartXAxe.max !== undefined) {
            if (Number(data.x) > Number(master.chartXAxe.max)) {
              break;
            }
            endIndex = i;
          }
        }
      }
      if (beginIndex !== null) {
        slave.chartXAxe.min = (
          slave.chartDataSets[0].data[beginIndex] as Point
        ).x;
      }
      if (endIndex !== null) {
        slave.chartXAxe.max = (
          slave.chartDataSets[0].data[endIndex] as Point
        ).x;
      }
    }
  }

  public createXAxis(xAxeType: string): ScaleOptions {
    if (xAxeType === 'linear') {
      const xa: ScaleOptions = {
        type: 'linear',
      };
      return xa;
    }
    if (xAxeType === 'time') {
      const xa: ScaleOptions = {
        type: 'time',
        time: {
          unit: 'minute',
          tooltipFormat: 'YYYY-MM-DD HH:mm:ss',
          displayFormats: {
            minute: 'H:mm',
          },
        },
      };
      return xa;
    }
    return null;
  }

  public createChartDataSets(): ChartDataset {
    const chartData: ChartDataset = {
      borderColor: 'black',
      label: 'label',
      yAxisID: 'yAxisID',
      data: [],
      fill: false,
      cubicInterpolationMode: 'monotone',
      tension: 0,
      pointRadius: 0,
      borderWidth: 1,
      pointHitRadius: 5,
      hidden: false,
    };
    return chartData;
  }

  public createYAxisProperties(
    theIndex: number,
    theColor: string,
    theHiddenColor: string,
    theVisibility: boolean
  ): ChartYAxisProperties {
    const props: ChartYAxisProperties = {
      index: theIndex,
      color: theVisibility ? theColor : theHiddenColor,
      hiddenColor: theHiddenColor,
      min: undefined,
      max: undefined,
      visibility: theVisibility,
    };
    return props;
  }

  public createYAxis(
    axisId: string,
    label: string,
    color: string,
    yPosition: 'left' | 'right'
  ): ScaleOptions {
    const ya: ScaleOptions = {
      type: 'linear',
      position: yPosition,
      title: {
        display: true,
        text: label,
        color,
      },
      ticks: {
        color,
      },
    };
    return ya;
  }
}
